import React from 'react';

import { IconX } from '@tabler/icons-react';
import {
  Backdrop,
  Fade,
  Modal as MUIModal,
  ModalProps as MUIModalProps,
  Stack,
} from '@mui/material';

import { BaseColorKey, NotifierColorKey, PureColorKey, SubjectColorKey } from '../../theme';
import Button from '../Button';

import {
  ButtonBar,
  ChildBox,
  CloseIconButton,
  Container,
  Description,
  Title,
  TitleContainer,
} from './index.styles';

export type ModalButton = {
  label: string;
  onClick?: () => void;
  color?: BaseColorKey | NotifierColorKey | PureColorKey;
  closeOnClick?: boolean;
  loading?: boolean;
  disabled?: boolean;
};

interface ModalProps extends Omit<MUIModalProps, 'children' | 'onClose'> {
  open: boolean;
  handleClose?: (event?: object, reason?: string) => void;
  hideCloseIcon?: boolean;
  title?: string;
  titleColor?: BaseColorKey | SubjectColorKey | NotifierColorKey;
  description?: string;
  buttons?: ModalButton[];
  children?: React.ReactNode;
  scrollY?: boolean;
  /** Fixed width in px, still clamped to 95% of the viewport and floored at
      the 400px min-width. Without one the modal shrink-wraps its content, so
      anything that swaps the content — a tab change, data loading in —
      resizes it. */
  width?: number;
  /** Drop the surface — no background, shadow or padding — so the children sit
      directly on the backdrop and provide their own edges. */
  bare?: boolean;
}

const Modal = ({
  open = false,
  handleClose = async () => {},
  hideCloseIcon = false,
  title,
  children = <div />,
  description,
  buttons = [],
  titleColor = 'gray',
  scrollY,
  width,
  bare = false,
  ...props
}: ModalProps) => (
  <MUIModal
    open={open}
    onClose={handleClose}
    closeAfterTransition
    BackdropComponent={Backdrop}
    BackdropProps={{
      timeout: 500,
    }}
    {...props}
  >
    <Fade in={open}>
      <Container $bare={bare} $width={width}>
        {!hideCloseIcon && (
          <CloseIconButton aria-label='Close dialog' onClick={handleClose}>
            <IconX />
          </CloseIconButton>
        )}
        {title && (
          <TitleContainer color={titleColor}>
            <Title variant='h5'>{title}</Title>
          </TitleContainer>
        )}
        {description && (
          <Description>
            <div>
              {description.split('\n').map(line => (
                <p key={`line-${line}`}>{line}</p>
              ))}
            </div>
          </Description>
        )}
        {children && (
          <ChildBox $scrollY={scrollY} $bare={bare}>
            {children}
          </ChildBox>
        )}
        {buttons.length > 0 && (
          <ButtonBar>
            <Stack direction='row' spacing={1}>
              {buttons.map(button => (
                <Button
                  key={`${button.label}${button?.color}`}
                  loading={button?.loading}
                  onClick={async () => {
                    try {
                      await button.onClick?.();
                    } catch (error) {
                      // A rejected handler must not escape as an unhandled rejection;
                      // callers surface their own errors and the modal stays put
                      console.error(error);
                      return;
                    }
                    if (button?.closeOnClick) {
                      await handleClose();
                    }
                  }}
                  color={button?.color}
                  disabled={button?.disabled}
                  size='small'
                  variant='text'
                  allCaps
                >
                  {button.label}
                </Button>
              ))}
            </Stack>
          </ButtonBar>
        )}
      </Container>
    </Fade>
  </MUIModal>
);

export default Modal;
